// src/components/BatchVerifyButton.jsx
// v9 : gère le statut 'manual_check' renvoyé par les domaines bloqués (DataDome).

import React, { useState, useMemo } from 'react';
import { supabase } from '../supabase';

const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const CONCURRENCY = 1;

function getListingUrl(maison) {
  return maison?.lienAnnonce || maison?.url || maison?.sourceUrl || null;
}

function isConclusive(status) {
  return status === 'active' || status === 'removed' || status === 'manual_check';
}

async function callCheckAPI(url, lastCheckedAt, lastStatus, accessToken) {
  const res = await fetch('/api/check-listing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
    body: JSON.stringify({ url, lastCheckedAt: lastCheckedAt || null, lastStatus: lastStatus || null }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

async function runWithConcurrency(items, limit, fn, onProgress) {
  let cursor = 0;
  let done = 0;
  const results = new Array(items.length);
  const workers = Array.from({ length: limit }, async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= items.length) return;
      results[idx] = await fn(items[idx], idx);
      done++;
      onProgress?.(done, items.length);
    }
  });
  await Promise.all(workers);
  return results;
}

export function BatchVerifyButton({ maisons, onMaisonVerified, isMobile = false }) {
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState([]);

  const { toCheck, skipped } = useMemo(() => {
    const eligible = [];
    let skip = 0;
    for (const m of maisons || []) {
      if (!getListingUrl(m)) continue;
      if (isConclusive(m.listing_status) && m.listing_status_checked_at) {
        const elapsed = Date.now() - new Date(m.listing_status_checked_at).getTime();
        if (elapsed < COOLDOWN_MS) {
          skip++;
          continue;
        }
      }
      eligible.push(m);
    }
    return { toCheck: eligible, skipped: skip };
  }, [maisons]);

  const isRunning = progress !== null;
  const canRun = !isRunning && toCheck.length > 0;

  const handleClick = async () => {
    if (!canRun) return;
    setSummary(null);
    setDetails([]);
    setProgress({ done: 0, total: toCheck.length });

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      setProgress(null);
      setDetails([{ titre: 'Erreur', error: 'Session expirée, reconnecte-toi puis réessaie.' }]);
      return;
    }

    const collected = [];

    const results = await runWithConcurrency(
      toCheck,
      CONCURRENCY,
      async (maison) => {
        try {
          const url = getListingUrl(maison);
          const json = await callCheckAPI(url, maison.listing_status_checked_at, maison.listing_status, accessToken);

          // Détermination du nouveau statut
          let newStatus;
          if (json.manualCheck) {
            newStatus = 'manual_check';
          } else if (json.active === true) {
            newStatus = 'active';
          } else if (json.active === false) {
            newStatus = 'removed';
          } else {
            newStatus = 'unknown';
          }

          collected.push({
            titre: maison.titre || maison.ville || `#${maison.id}`,
            status: newStatus,
            debug: json._debug,
          });

          const updated = {
            ...maison,
            listing_status: newStatus,
            listing_status_checked_at: json.checkedAt,
          };
          await onMaisonVerified?.(updated);

          return { id: maison.id, status: newStatus };
        } catch (e) {
          collected.push({
            titre: maison.titre || maison.ville || `#${maison.id}`,
            error: e.message,
          });
          return { id: maison.id, error: e.message };
        }
      },
      (done, total) => setProgress({ done, total })
    );

    const counts = { active: 0, removed: 0, manual_check: 0, unknown: 0, error: 0 };
    for (const r of results) {
      if (r.error) counts.error++;
      else if (r.status === 'active') counts.active++;
      else if (r.status === 'removed') counts.removed++;
      else if (r.status === 'manual_check') counts.manual_check++;
      else counts.unknown++;
    }
    setProgress(null);
    setSummary(counts);
    setDetails(collected);

    console.log('[BatchVerify] Résultats détaillés :', collected);
  };

  const buttonStyle = {
    padding: isMobile ? '8px 12px' : '9px 14px',
    borderRadius: 9,
    border: '1px solid rgba(96, 165, 250, 0.3)',
    background: canRun ? 'rgba(96, 165, 250, 0.08)' : 'rgba(96, 165, 250, 0.04)',
    color: '#60a5fa',
    cursor: canRun ? 'pointer' : 'not-allowed',
    fontWeight: 600,
    fontSize: 13,
    opacity: canRun ? 1 : 0.6,
    whiteSpace: 'nowrap',
  };

  const detailsTooltip = details
    .map((d) => {
      if (d.error) return `❌ ${d.titre}: ${d.error}`;
      const icon =
        d.status === 'removed' ? '⚠' :
        d.status === 'active' ? '✓' :
        d.status === 'manual_check' ? '🔍' : '?';
      return `${icon} ${d.titre}: ${d.status}`;
    })
    .join('\n');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button onClick={handleClick} disabled={!canRun} style={buttonStyle}>
        {isRunning
          ? `Vérification ${progress.done}/${progress.total}…`
          : `🔄 Tout vérifier${toCheck.length ? ` (${toCheck.length})` : ''}`}
      </button>

      {!isRunning && skipped > 0 && (
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          {skipped} récente(s)
        </span>
      )}

      {summary && !isRunning && (
        <div
          style={{ display: 'flex', gap: 10, fontSize: 12, flexWrap: 'wrap', alignItems: 'center', cursor: 'help' }}
          title={detailsTooltip}
        >
          {summary.active > 0 && <span style={{ color: '#22c55e' }}>✓ {summary.active}</span>}
          {summary.removed > 0 && <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠ {summary.removed} retirée(s)</span>}
          {summary.manual_check > 0 && <span style={{ color: '#f59e0b', fontWeight: 600 }}>🔍 {summary.manual_check} à vérifier</span>}
          {summary.unknown > 0 && <span style={{ color: '#94a3b8' }}>? {summary.unknown}</span>}
          {summary.error > 0 && <span style={{ color: '#f59e0b' }}>✕ {summary.error}</span>}
        </div>
      )}
    </div>
  );
}
